import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/ui/Navbar";
import { toast } from "react-toastify";
import { Card, CardContent } from "../components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { LoaderPinwheel, ArrowLeft, AlertTriangle, Unlock } from "lucide-react";
import ModalLiberarServicio from "../components/modals/ModalLiberarServicio";

const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed"];

const EmpresaDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [eliminando, setEliminando] = useState(false);
  const [usuarioLiberar, setUsuarioLiberar] = useState(null);
  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  const cargarDetalle = () =>
    axios
      .get(`${API}/dashboardGlobal/empresa/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Error al cargar el detalle de la empresa"));

  useEffect(() => {
    cargarDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const eliminarEmpresa = async () => {
    if (confirmText.trim() !== data.empresa.nit) {
      toast.warning("El NIT ingresado no coincide");
      return;
    }
    setEliminando(true);
    try {
      const res = await fetch(`${API}/company/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || "No se pudo eliminar");
      toast.success("Empresa eliminada correctamente");
      navigate("/adminDashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || "Error al eliminar la empresa");
    } finally {
      setEliminando(false);
    }
  };

  const formatearFecha = (fecha) =>
    fecha
      ? new Date(fecha).toLocaleDateString("es-CO", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "No registrado";

  const formatearFechaHora = (fecha) =>
    fecha
      ? new Date(fecha).toLocaleString("es-CO", {
          dateStyle: "long",
          timeStyle: "short",
        })
      : "Nunca ha iniciado sesión";

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 pt-20 md:pt-28">
          <button
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            onClick={() => navigate("/adminDashboard")}
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
        </div>
        <div className="h-screen flex items-center justify-center">
          <LoaderPinwheel className="animate-spin w-10 h-10 text-blue-600" />
        </div>
      </div>
    );
  }

  const {
    empresa,
    usuariosEmpresa,
    liberacionesServicio = [],
    indicadores,
    equiposPorPropiedad,
    cronogramasPorEstado,
    gestionesPorMes,
    puedeEliminar,
  } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 md:px-6 pt-20 md:pt-28 pb-10 space-y-6">
        <button
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          onClick={() => navigate("/adminDashboard")}
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <Card>
          <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800">
                {empresa.razon_social}
              </h1>
              <p className="text-sm text-gray-500">
                NIT {empresa.nit} · {empresa.correo}
              </p>
            </div>
            <div className="flex gap-2">
              <span
                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  empresa.activa
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {empresa.activa ? "Activa" : "Inactiva"}
              </span>
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                {empresa.plan}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">Información de la empresa</h2>
            <div className="flex flex-col md:flex-row gap-6">
              {empresa.logo && (
                <img
                  src={empresa.logo}
                  alt={`Logo de ${empresa.razon_social}`}
                  className="w-24 h-24 object-contain rounded-lg border border-gray-200 self-start"
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 text-sm">
                <div>
                  <p className="text-gray-500">Dirección</p>
                  <p className="text-gray-800">{empresa.direccion || "No registrado"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Teléfono</p>
                  <p className="text-gray-800">{empresa.telefono || "No registrado"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Correo</p>
                  <p className="text-gray-800">{empresa.correo || "No registrado"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Licencia</p>
                  <p className="text-gray-800">{empresa.licencia || "No registrado"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Fecha de registro</p>
                  <p className="text-gray-800">{formatearFecha(empresa.createdAt)}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-4 pt-4">
              <h3 className="font-semibold mb-2 text-sm text-gray-700">
                Administrador
              </h3>
              {empresa.administrador ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Nombre</p>
                    <p className="text-gray-800">{empresa.administrador.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Correo</p>
                    <p className="text-gray-800">{empresa.administrador.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Teléfono</p>
                    <p className="text-gray-800">
                      {empresa.administrador.telefono || "No registrado"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Último login</p>
                    <p className="text-gray-800">
                      {formatearFechaHora(empresa.administrador.lastLoginAt)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Sin administrador asignado</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">Usuarios de la empresa</h2>
            {usuariosEmpresa.length === 0 ? (
              <p className="text-sm text-gray-600">Sin usuarios registrados</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Nombre</th>
                      <th className="text-left px-3 py-2">Correo</th>
                      <th className="text-left px-3 py-2">Rol</th>
                      <th className="text-left px-3 py-2">Última conexión</th>
                      <th className="text-left px-3 py-2">Vencidos</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="text-left px-3 py-2">Servicio activo</th>
                      <th className="text-left px-3 py-2">Inicio</th>
                      <th className="text-left px-3 py-2">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosEmpresa.map((u) => (
                      <tr key={u._id} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-800">{u.name}</td>
                        <td className="px-3 py-2 text-gray-600">{u.email}</td>
                        <td className="px-3 py-2 text-gray-600">{u.rol}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {formatearFechaHora(u.lastLoginAt)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              u.vencidos > 0
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {u.vencidos}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {!u.servicio ? (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              Disponible
                            </span>
                          ) : u.servicio.valido ? (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                              En servicio
                            </span>
                          ) : (
                            <span
                              title={u.servicio.motivo || ""}
                              className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800"
                            >
                              Inconsistente
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {u.servicio ? (
                            <div>
                              <p className="text-gray-800">
                                Sí · {u.servicio.tipo || "—"}
                                {u.servicio.equipo ? ` · ${u.servicio.equipo}` : ""}
                              </p>
                              <p className="text-xs text-gray-500">
                                Estado: {u.servicio.estadoCronograma}
                              </p>
                              <p className="text-[10px] text-gray-400 break-all">
                                {u.servicio.cronogramaId}
                              </p>
                            </div>
                          ) : (
                            "No"
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {u.servicio?.inicio
                            ? new Date(u.servicio.inicio).toLocaleString("es-CO", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })
                            : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {u.servicio ? (
                            <button
                              onClick={() => setUsuarioLiberar(u)}
                              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap"
                            >
                              <Unlock className="w-3.5 h-3.5" /> Liberar servicio
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">Historial de liberaciones de servicio</h2>
            {liberacionesServicio.length === 0 ? (
              <p className="text-sm text-gray-600">Sin liberaciones registradas</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Usuario</th>
                      <th className="text-left px-3 py-2">Realizado por</th>
                      <th className="text-left px-3 py-2">Servicio</th>
                      <th className="text-left px-3 py-2">Estado anterior → posterior</th>
                      <th className="text-left px-3 py-2">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liberacionesServicio.map((l) => (
                      <tr key={l._id} className="border-t border-gray-100 align-top">
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {formatearFechaHora(l.fecha)}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{l.usuario}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {l.actor}
                          {l.accion === "servicio.liberacion_automatica" && (
                            <span className="block text-[10px] text-gray-400">automática</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[10px] text-gray-500 break-all">
                          {l.cronograma || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {l.estadoAnterior?.usuario || "—"} ({l.estadoAnterior?.cronogramaEstado || "—"})
                          {" → "}
                          {l.estadoPosterior?.usuario || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{l.motivo || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <ModalLiberarServicio
          open={!!usuarioLiberar}
          usuario={usuarioLiberar}
          onClose={() => setUsuarioLiberar(null)}
          onLiberado={cargarDetalle}
        />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ["Equipos", indicadores.equipos],
            ["Usuarios", indicadores.usuarios],
            ["Cronogramas", indicadores.cronogramas],
            ["Mantenimientos", indicadores.gestiones],
            ["Hojas de vida", indicadores.hojasVida],
          ].map(([label, value]) => (
            <Card key={label}>
              <CardContent className="text-center">
                <p className="text-gray-500 text-sm">{label}</p>
                <p className="text-2xl font-bold text-blue-600">{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">
              Equipos por tipo de propiedad
            </h2>
            <ResponsiveContainer height={260}>
              <PieChart>
                <Pie
                  data={equiposPorPropiedad}
                  dataKey="total"
                  nameKey="_id"
                  outerRadius={90}
                >
                  {equiposPorPropiedad.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">Cronogramas por estado</h2>
            <ResponsiveContainer height={260}>
              <BarChart data={cronogramasPorEstado}>
                <XAxis dataKey="_id" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h2 className="font-semibold mb-3">
              Mantenimientos por mes (últimos 12 meses)
            </h2>
            <ResponsiveContainer height={260}>
              <BarChart data={gestionesPorMes}>
                <XAxis dataKey="_id" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-red-200">
          <CardContent>
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Zona de eliminación
            </h2>
            {!puedeEliminar ? (
              <p className="text-sm text-gray-600">
                No se puede eliminar esta empresa porque tiene{" "}
                {indicadores.equipos} equipo(s) registrado(s) en inventario.
                Retira todos los equipos para habilitar la eliminación.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Esta acción borra la empresa y todos sus datos asociados
                  (usuarios, clientes, sedes, almacenes, proveedores,
                  sensores). No se puede deshacer.
                </p>
                <p className="text-sm text-gray-700">
                  Escribe el NIT{" "}
                  <span className="font-semibold">{empresa.nit}</span> para
                  confirmar:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 rounded-lg border border-gray-300 text-sm"
                />
                <button
                  disabled={eliminando || confirmText.trim() !== empresa.nit}
                  onClick={eliminarEmpresa}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm px-4 py-2 rounded-lg"
                >
                  {eliminando ? "Eliminando..." : "Eliminar empresa"}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmpresaDetallePage;
